import { createRng } from "../util/rng.js";
import { SECTION_SEC, sectionIndexAt } from "./music.js";

/**
 * Effects on the narration: band-limiting, saturation, bit-crushing, static
 * that rides the voice, signal dropouts, and an echo room.
 *
 * Like the music's arrangement, the treatment follows the wall clock. Each
 * section (shared with the music, so changes land on slot boundaries, where a
 * new disclosure starts) picks a treatment, from clear to a degraded
 * transmission, seeded by its index alone. Every listener hears the same
 * treatment at the same moment. Within a section, slow LFOs keep the band
 * edges, the echo and the static moving.
 *
 * Only graph-routable narrators (Kokoro) pass through this. Web Speech plays
 * outside the Web Audio graph and is heard clean.
 *
 * One persistent chain, scheduled incrementally: `update()` adds automation
 * for sections inside the lookahead. Nothing is rebuilt, so nothing clicks.
 */

export type VoiceTreatment = "clear" | "radio" | "transmission" | "lofi" | "distant";

export interface VoiceFxPlan {
  index: number;
  treatment: VoiceTreatment;
  /** Band edges, Hz. */
  lowHz: number;
  highHz: number;
  /** Saturation mix, 0..1. */
  drive: number;
  /** Bit-crusher mix, 0..1. */
  crush: number;
  /** Static that follows the voice's loudness, 0..1. */
  hiss: number;
  /** Static that is there even between words, 0..1. */
  floor: number;
  /** Brief signal losses per minute. */
  dropoutsPerMinute: number;
  /** Echo and room send, 0..1. */
  space: number;
}

/** Clear and radio are the most common, so the voice is usually easy to follow. */
const TREATMENTS: readonly VoiceTreatment[] = [
  "clear",
  "clear",
  "clear",
  "radio",
  "radio",
  "radio",
  "transmission",
  "transmission",
  "lofi",
  "lofi",
  "distant",
  "distant",
];

/**
 * A plan that leaves the voice alone.
 *
 * Used for an artifact whose own rendering already hisses or gates: adding the
 * saturator, bit-crusher, static and dropouts on top of that is what made the
 * half-precision voice unintelligible. The band edges and a little room remain.
 */
export function plainPlan(index: number): VoiceFxPlan {
  return {
    index,
    treatment: "clear",
    lowHz: 80,
    highHz: 12_000,
    drive: 0,
    crush: 0,
    hiss: 0,
    floor: 0,
    dropoutsPerMinute: 0,
    space: 0.08,
  };
}

export function voiceFxPlan(index: number, plain = false): VoiceFxPlan {
  if (plain) return plainPlan(index);
  const rng = createRng(`voice:section:${index}`);
  const treatment = rng.pick(TREATMENTS);
  const r = (min: number, max: number) => rng.range(min, max);
  const base = { index, treatment, drive: 0, crush: 0, hiss: 0, floor: 0, dropoutsPerMinute: 0 };
  switch (treatment) {
    case "clear":
      return { ...base, lowHz: r(70, 110), highHz: r(9_000, 12_000), space: r(0.05, 0.15) };
    case "radio":
      return { ...base, lowHz: r(250, 400), highHz: r(2_800, 3_800), drive: r(0.3, 0.6), hiss: r(0.2, 0.45), space: r(0.05, 0.15) };
    case "transmission":
      return {
        ...base,
        lowHz: r(350, 500),
        highHz: r(2_400, 3_200),
        drive: r(0.5, 0.85),
        hiss: r(0.5, 0.9),
        floor: r(0.1, 0.3),
        dropoutsPerMinute: r(2, 6),
        space: r(0.1, 0.2),
      };
    case "lofi":
      return { ...base, lowHz: r(150, 250), highHz: r(4_000, 6_000), drive: r(0.1, 0.3), crush: r(0.35, 0.6), hiss: r(0.1, 0.25), space: r(0.05, 0.15) };
    case "distant":
      return { ...base, lowHz: r(150, 250), highHz: r(5_000, 7_000), hiss: 0.1, space: r(0.5, 0.8) };
  }
}

/**
 * Gain correction, dB. Up to +3.5 dB as the low cut climbs past the voice's
 * fundamental; down 3 dB per unit of echo send.
 */
export function trimDb(plan: VoiceFxPlan): number {
  return Math.min(3.5, Math.max(0, (plan.lowHz - 100) / 100)) - 3 * plan.space;
}

/** How far ahead `update()` schedules. Covers a throttled background-tab timer. */
export const VOICE_FX_LOOKAHEAD_SEC = 300;
/** Time constant for treatment changes: settled within a second of the boundary. */
const CHANGE_TAU_SEC = 0.3;

// Levels, calibrated by rendering a test voice: static at full `hiss` sits
// roughly 8-10 dB under the voice (a transmission), lighter radio hiss about
// 15 dB under, and every treatment stays within about 2 dB of clear so a change
// of treatment is not a change of volume.
const HISS_SCALE = 3.5;
const FLOOR_LEVEL = 0.35;
const BURST_LEVEL = 0.12;
const SPACE_SEND = 0.6;
const DRIVE_MAKEUP = 0.8;
const CRUSH_BITS = 5;

function curve(points: number, shape: (x: number) => number): Float32Array<ArrayBuffer> {
  const data = new Float32Array(points);
  for (let i = 0; i < points; i += 1) data[i] = shape((i / (points - 1)) * 2 - 1);
  return data;
}

function noiseBuffer(context: BaseAudioContext, seconds: number): AudioBuffer {
  const length = Math.floor(context.sampleRate * seconds);
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const data = buffer.getChannelData(0);
  const rng = createRng("voice:static");
  for (let i = 0; i < length; i += 1) {
    // Hiss, with the odd crackle.
    data[i] = (rng.next() * 2 - 1) * 0.5 + (rng.chance(0.0006) ? (rng.next() * 2 - 1) * 1.2 : 0);
  }
  return buffer;
}

function roomResponse(context: BaseAudioContext, seconds: number): AudioBuffer {
  const length = Math.floor(context.sampleRate * seconds);
  const buffer = context.createBuffer(2, length, context.sampleRate);
  const rng = createRng("voice:room");
  for (let channel = 0; channel < 2; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < length; i += 1) data[i] = (rng.next() * 2 - 1) * 0.08 * Math.pow(1 - i / length, 3);
  }
  return buffer;
}

export class VoiceFx {
  /** Narrators connect here. */
  readonly input: GainNode;
  private readonly output: GainNode;
  private readonly sources: AudioScheduledSourceNode[] = [];
  private readonly nodes: AudioNode[] = [];
  private readonly params: Array<{ param: AudioParam; value: (plan: VoiceFxPlan) => number }> = [];
  private readonly signal: GainNode;
  private readonly burst: GainNode;
  /** Last section with automation scheduled. */
  private through: number | null = null;
  private plain = false;

  constructor(context: BaseAudioContext, destination: AudioNode) {
    const node = <T extends AudioNode>(created: T): T => {
      this.nodes.push(created);
      return created;
    };
    const lfo = (param: AudioParam, rateHz: number, depth: number) => {
      const oscillator = context.createOscillator();
      oscillator.frequency.value = rateHz;
      const amount = node(context.createGain());
      amount.gain.value = depth;
      oscillator.connect(amount);
      amount.connect(param);
      oscillator.start();
      this.sources.push(oscillator);
    };
    const follow = (param: AudioParam, value: (plan: VoiceFxPlan) => number) => this.params.push({ param, value });

    this.input = node(context.createGain());
    this.output = node(context.createGain());
    this.output.connect(destination);

    // --- band ---------------------------------------------------------------
    const low = node(context.createBiquadFilter());
    low.type = "highpass";
    low.Q.value = 0.7;
    follow(low.frequency, (plan) => plan.lowHz);
    const high = node(context.createBiquadFilter());
    high.type = "lowpass";
    high.Q.value = 0.9;
    follow(high.frequency, (plan) => plan.highHz);
    // A slow wander of the upper edge: the "tuning" never quite holds still.
    lfo(high.frequency, 0.07, 180);
    this.input.connect(low);
    low.connect(high);

    // --- saturation, mixed in parallel so "clear" is untouched -------------
    const dry = node(context.createGain());
    follow(dry.gain, (plan) => 1 - plan.drive);
    const drivePre = node(context.createGain());
    drivePre.gain.value = 1.4;
    const shaper = node(context.createWaveShaper());
    shaper.curve = curve(2049, (x) => Math.tanh(3 * x) / Math.tanh(3));
    shaper.oversample = "2x";
    const driveMix = node(context.createGain());
    follow(driveMix.gain, (plan) => plan.drive * DRIVE_MAKEUP);
    high.connect(dry);
    high.connect(drivePre);
    drivePre.connect(shaper);
    shaper.connect(driveMix);
    const driven = node(context.createGain());
    dry.connect(driven);
    driveMix.connect(driven);

    // --- bit-crusher ---------------------------------------------------------
    const steps = Math.pow(2, CRUSH_BITS - 1);
    const crusher = node(context.createWaveShaper());
    crusher.curve = curve(8193, (x) => Math.round(x * steps) / steps);
    const clean = node(context.createGain());
    follow(clean.gain, (plan) => 1 - plan.crush);
    const crushMix = node(context.createGain());
    follow(crushMix.gain, (plan) => plan.crush);
    driven.connect(clean);
    driven.connect(crusher);
    crusher.connect(crushMix);

    // --- the signal, which dropouts interrupt -------------------------------
    this.signal = node(context.createGain());
    // Level-match the treatments: a narrow band loses the voice's fundamental,
    // and a big echo adds energy, so each is trimmed back towards clear.
    const trim = node(context.createGain());
    follow(trim.gain, (plan) => Math.pow(10, trimDb(plan) / 20));
    clean.connect(trim);
    crushMix.connect(trim);
    trim.connect(this.signal);
    this.signal.connect(this.output);

    // --- static ----------------------------------------------------------------
    const noise = context.createBufferSource();
    noise.buffer = noiseBuffer(context, 3);
    noise.loop = true;
    noise.start();
    this.sources.push(noise);
    const noiseBand = node(context.createBiquadFilter());
    noiseBand.type = "bandpass";
    noiseBand.frequency.value = 2_500;
    noiseBand.Q.value = 0.5;
    lfo(noiseBand.frequency, 0.11, 700);
    noise.connect(noiseBand);

    // Static that rides the voice: rectify, smooth, and use that as its gain.
    const rectifier = node(context.createWaveShaper());
    rectifier.curve = curve(2049, Math.abs);
    const envelope = node(context.createBiquadFilter());
    envelope.type = "lowpass";
    envelope.frequency.value = 6;
    const envelopeScale = node(context.createGain());
    follow(envelopeScale.gain, (plan) => plan.hiss * HISS_SCALE);
    const hissGate = node(context.createGain());
    hissGate.gain.value = 0;
    this.input.connect(rectifier);
    rectifier.connect(envelope);
    envelope.connect(envelopeScale);
    envelopeScale.connect(hissGate.gain);
    noiseBand.connect(hissGate);
    hissGate.connect(this.output);

    const floor = node(context.createGain());
    follow(floor.gain, (plan) => plan.floor * FLOOR_LEVEL);
    noiseBand.connect(floor);
    floor.connect(this.output);

    // Interference bursts during dropouts.
    this.burst = node(context.createGain());
    this.burst.gain.value = 0;
    noiseBand.connect(this.burst);
    this.burst.connect(this.output);

    // --- space: a short echo and a small room ---------------------------------
    const spaceSend = node(context.createGain());
    follow(spaceSend.gain, (plan) => plan.space * SPACE_SEND);
    this.signal.connect(spaceSend);
    const echo = node(context.createDelay(1));
    echo.delayTime.value = 0.21;
    lfo(echo.delayTime, 0.05, 0.006);
    const echoTone = node(context.createBiquadFilter());
    echoTone.type = "lowpass";
    echoTone.frequency.value = 3_000;
    const feedback = node(context.createGain());
    feedback.gain.value = 0.3;
    spaceSend.connect(echo);
    echo.connect(echoTone);
    echoTone.connect(feedback);
    feedback.connect(echo);
    echoTone.connect(this.output);
    const room = node(context.createConvolver());
    room.normalize = false;
    room.buffer = roomResponse(context, 0.9);
    spaceSend.connect(room);
    room.connect(this.output);
  }

  /**
   * Schedule treatment changes up to `VOICE_FX_LOOKAHEAD_SEC` ahead.
   * `wallClockMs` is the wall clock at `contextNow`. Call it at least once per
   * slot; calling it more often is harmless.
   */
  update(contextNow: number, wallClockMs: number): void {
    const wallSec = wallClockMs / 1000;
    const current = sectionIndexAt(wallSec);
    const toContext = (sec: number) => contextNow + (sec - wallSec);

    if (this.through === null || this.through < current) {
      // First call, or the schedule lapsed (a tab hidden past the lookahead).
      this.apply(voiceFxPlan(current, this.plain), contextNow, this.through === null);
      this.scheduleDropouts(current, toContext(current * SECTION_SEC), contextNow);
      this.through = current;
    }
    const last = sectionIndexAt(wallSec + VOICE_FX_LOOKAHEAD_SEC);
    for (let index = this.through + 1; index <= last; index += 1) {
      const at = toContext(index * SECTION_SEC);
      this.apply(voiceFxPlan(index, this.plain), at);
      this.scheduleDropouts(index, at, contextNow);
      this.through = index;
    }
  }

  dispose(): void {
    for (const source of this.sources) {
      try {
        source.stop();
      } catch {
        // already stopped
      }
      source.disconnect();
    }
    for (const node of this.nodes) node.disconnect();
  }

  /**
   * Scale the whole effect output, used to level-match a quieter artifact.
   *
   * After the effects on purpose: the chain contains a saturator and static
   * that follows the voice envelope, so changing the level before it would
   * change how much distortion and hiss the listener hears.
   */
  setTrim(multiplier: number): void {
    const gain = this.output.gain;
    const now = this.output.context.currentTime;
    gain.cancelScheduledValues(now);
    gain.setTargetAtTime(multiplier, now, 0.02);
  }

  /** Run the effects in clear-only mode, for an artifact that already degrades. */
  setPlain(plain: boolean): void {
    this.plain = plain;
  }

  private apply(plan: VoiceFxPlan, at: number, immediate = false): void {
    for (const { param, value } of this.params) {
      // The first treatment is set outright: gliding from the nodes' defaults
      // would put static and saturation on the first words of a clear section.
      if (immediate) param.setValueAtTime(value(plan), at);
      else param.setTargetAtTime(value(plan), at, CHANGE_TAU_SEC);
    }
  }

  private scheduleDropouts(index: number, sectionAt: number, notBefore: number): void {
    for (const dropout of voiceDropouts(index)) {
      const at = sectionAt + dropout.offsetSec;
      if (at < notBefore + 0.05) continue;
      const { lengthSec: length, depth } = dropout;
      const gain = this.signal.gain;
      gain.setValueAtTime(1, at);
      gain.linearRampToValueAtTime(depth, at + 0.012);
      gain.setValueAtTime(depth, at + length);
      gain.linearRampToValueAtTime(1, at + length + 0.03);
      const burst = this.burst.gain;
      burst.setValueAtTime(0, at);
      burst.linearRampToValueAtTime(BURST_LEVEL, at + 0.01);
      burst.setValueAtTime(BURST_LEVEL, at + length);
      burst.linearRampToValueAtTime(0, at + length + 0.04);
    }
  }
}

export interface VoiceDropout {
  /** Seconds after the section starts. */
  offsetSec: number;
  lengthSec: number;
  /** Signal gain while it lasts. */
  depth: number;
}

/** A section's dropouts, seeded by its index alone so every listener hears the same ones. */
export function voiceDropouts(index: number, plain = false): VoiceDropout[] {
  const plan = voiceFxPlan(index, plain);
  if (plan.dropoutsPerMinute <= 0) return [];
  const rng = createRng(`voice:section:${index}:dropouts`);
  const meanGap = 60 / plan.dropoutsPerMinute;
  const dropouts: VoiceDropout[] = [];
  for (let offset = rng.range(0.3, 1.5) * meanGap; offset < SECTION_SEC; offset += rng.range(0.5, 1.5) * meanGap) {
    dropouts.push({ offsetSec: offset, lengthSec: rng.range(0.05, 0.18), depth: rng.range(0.03, 0.25) });
  }
  return dropouts;
}
